/**
 * 通知深链映射表 —— `ref_type → 前端路由` 的唯一登记处。
 *
 * 登记义务（实施计划 R4 裁决）：后端新增通知的 ref_type 时必须在此补一行，
 * 不允许散落到组件内 if/else。未登记的 ref_type 前端降级为纯文本展示
 * （不渲染跳转），不影响通知本体可读。
 *
 * 路由路径以 apps/jerry/src/routes.tsx 为准：
 * - jerry.meeting        → /meeting-center（会议中心；选中态目前未 URL 化，
 *                          深链先落到会议列表页，ref_id 暂不消费）
 * - jerry.matter         → /matters/:id（事项详情）
 * - jerry.pipeline_execution → /pipelines/:id（工作流执行详情，:id 为 execution.id）
 * - jerry.scheduled_task → /scheduled-tasks（定时任务列表；产物详情未单页化）
 * - jerry.agent_run      → /agent-runs/:id（长程运行详情页，切片 2 落地；
 *                          ref_id = agent_run.id；缺 ref_id 时落 /scheduled-tasks
 *                          ——/agent-runs 列表路由不存在，兜底去发起入口
 *                          所在的定时任务页）
 * - jerry.draft_doc      → /drafts/:draftId（起草工作台；一键成稿/起草轮次
 *                          通知深链直达，起草域长程生成解耦一期 T3）。缺
 *                          ref_id 落 /drafts（草稿箱列表）
 */

/** 已知 ref_type 缺 ref_id 时的降级：能定位列表页就落列表页。 */
export type NotificationRouteBuilder = (refId: string | null) => string | null;

export const NOTIFICATION_REF_TYPE_ROUTES: Record<string, NotificationRouteBuilder> = {
  "jerry.meeting": () => "/meeting-center",
  "jerry.matter": (refId) => (refId ? `/matters/${refId}` : "/matters"),
  "jerry.pipeline_execution": (refId) => (refId ? `/pipelines/${refId}` : "/pipelines"),
  "jerry.scheduled_task": () => "/scheduled-tasks",
  "jerry.agent_run": (refId) => (refId ? `/agent-runs/${refId}` : "/scheduled-tasks"),
  "jerry.draft_doc": (refId) => (refId ? `/drafts/${refId}` : "/drafts"),
};

/**
 * 解析一条通知的深链路由。
 * 返回 null = 不可跳转（ref_type 未登记或缺失），调用方降级纯文本。
 */
export function resolveNotificationRoute(
  refType: string | null,
  refId: string | null,
): string | null {
  if (!refType) return null;
  const builder = NOTIFICATION_REF_TYPE_ROUTES[refType];
  if (!builder) return null;
  return builder(refId);
}
